"use client";

import Image from "next/image";
import { type CSSProperties, type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TwinMark } from "@product/components/app/twin/twin-mark";

import { DATA_LABEL_CLS, DISPLAY } from "@/components/editorial";
import { runOnceTwinClock } from "@/components/editorial/glass-twin/sequence";
import { TwinClockProvider } from "@/components/editorial/glass-twin/sequence-context";
import { enter, FRAME_CORNERS, UiRoot, ui } from "@/components/moments/ui";
import { useTimeline } from "@/components/moments/use-timeline";

import { LiveInstrument } from "../bands/hero-instrument";
import { TWIN_PIGMENTS } from "../glass-twin/particle-geometry";
import { FrameContents } from "@/components/product-shot/frame-contents";
import { NORTHSTAR_ANSWER } from "./answer-content";
import { ConnectedAnswer } from "./connected-answer";
import styles from "./story-steps.module.css";
import { CHAPTERS, SOURCES } from "./story-sources";

/*
 * The home's Twin story with every step in view at once (Archie,
 * 2026-10-01), where the scroll story showed one chapter at a time over
 * three screens of scrolling: scattered sources, one customer followed
 * across them, the shared understanding, and the answer in the tools a team
 * uses. Two rows of two, so each step has room: two drawn stages, the Twin
 * itself gathering into its rings (the hero's own particles, so the two
 * match), and the scroll story's stack of tool cards (connected-answer) at
 * its own size. Each step builds once, as it scrolls into view, and then
 * stands with the others. The words are the scroll story's own chapters
 * (story-sources.ts); its fifth, the workspace review, is left out. The
 * sector pages keep the scroll story.
 *
 * The first three stages are decoration to assistive technology: each
 * step's words say what its stage shows. The card stack is the reader's to
 * use.
 */

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

/* ------------------------------------------------------------------ */
/*  The evidence, as dots                                              */
/* ------------------------------------------------------------------ */

const noise = (seed: number) => {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
};
const dot = (x: number, y: number, r: number) =>
  `M${(x - r).toFixed(1)} ${y.toFixed(1)}a${r.toFixed(1)} ${r.toFixed(1)} 0 1 0 ${(2 * r).toFixed(1)} 0a${r.toFixed(1)} ${r.toFixed(1)} 0 1 0 ${(-2 * r).toFixed(1)} 0`;

/** The wide stage the sources are scattered and followed on, in SVG units. */
const WIDE = 520;
const TALL = 330;

/** How many dots of each pigment lie scattered on a stage. */
const DOTS_PER_PIGMENT = 190;

/**
 * The Twin's six pigments as fine dots scattered across the stage: the
 * hero's own particles, before anything has gathered them. The third step
 * shows them gathered, by the hero's renderer itself.
 */
const SCATTER = TWIN_PIGMENTS.map((color, layer) => {
  let path = "";
  for (let index = 0; index < DOTS_PER_PIGMENT; index += 1) {
    const seed = layer * 419 + index;
    path += dot(14 + noise(seed + 9) * (WIDE - 28), 14 + noise(seed + 18) * (TALL - 28), 1 + noise(seed + 4) * 1.25);
  }
  return { color, path };
});

/**
 * The scattered dots on a stage. They are drawn in the browser, as the stage
 * nears the viewport: the six paths are about 68KB of markup and two stages
 * show them, so served with the page they were a third of the home's HTML.
 */
function ScatteredDots({ opacity }: { opacity: number }) {
  return (
    <FrameContents>
      <svg viewBox={`0 0 ${WIDE} ${TALL}`} className="absolute inset-0 h-full w-full" style={{ opacity, transition: "opacity 600ms ease-out" }}>
        {SCATTER.map((layer) => (
          <path key={layer.color} fill={layer.color} d={layer.path} />
        ))}
      </svg>
    </FrameContents>
  );
}

/* ------------------------------------------------------------------ */
/*  Sources                                                            */
/* ------------------------------------------------------------------ */

function iconOf(source: string): string {
  const found = SOURCES.find((candidate) => candidate.source === source);
  if (!found) throw new Error(`The story has no source "${source}".`);
  return found.icon;
}

/** A record in its system, and where its chip sits: per cent of the stage across and down, and its tilt in degrees. `wide` chips only show where the stage is wide enough for all of them. */
type Placed = { source: string; label: string; at: readonly [x: number, y: number, tilt: number]; wide?: true };

/** A record in its source system: the system's mark and what the record is. */
function Chip({ source, label, at, wide, shown, delay = 0 }: Placed & { shown: boolean; delay?: number }) {
  return (
    <span className={`absolute ${wide ? "max-sm:hidden" : ""}`} style={{ left: `${at[0]}%`, top: `${at[1]}%`, transform: `translate(-50%, -50%) rotate(${at[2]}deg)` }}>
      <span
        {...FRAME_CORNERS}
        className={`${ui.panel} flex items-center gap-1.5 py-1 pr-2.5 pl-1 text-[11px] leading-none font-semibold whitespace-nowrap sm:gap-2 sm:py-1.5 sm:pr-3 sm:pl-1.5 sm:text-[12px]`}
        style={enter(shown, { delay, y: 6, duration: 500 })}
      >
        <span className="flex size-[18px] shrink-0 items-center justify-center bg-white shadow-[inset_0_0_0_1px_var(--ui-line)] sm:size-5">
          <Image src={`/concepts/source-icons/${iconOf(source)}`} width={14} height={14} alt="" className="size-3 object-contain sm:size-3.5" />
        </span>
        {label}
      </span>
    </span>
  );
}

/** The scattered sources: records of eight kinds, each in its own system, at the angles they were left at. */
const SCATTERED: readonly Placed[] = [
  { source: "Xero", label: "Invoices", at: [17, 13, -3] },
  { source: "Gmail", label: "Client emails", at: [50, 9, 2], wide: true },
  { source: "HubSpot", label: "Customer records", at: [80, 22, -2] },
  { source: "Linear", label: "Project update", at: [30, 42, 2] },
  { source: "SharePoint", label: "Contracts", at: [66, 50, -2], wide: true },
  { source: "Granola", label: "Meeting notes", at: [18, 74, -2] },
  { source: "Stripe", label: "Payments", at: [49, 84, 3], wide: true },
  { source: "Microsoft Teams", label: "Team conversations", at: [79, 80, 1] },
];

/** Northstar's four records, one in each of four systems, as the scroll story follows them. */
const FOLLOWED: readonly Placed[] = [
  { source: "HubSpot", label: "Northstar · Customer", at: [22, 20, -1] },
  { source: "Xero", label: "INV-2048 · £8,400", at: [78, 24, 1] },
  { source: "Linear", label: "Website · Delivered", at: [23, 78, 1] },
  { source: "Gmail", label: "Payment expected Friday", at: [75, 80, -1] },
];

/* ------------------------------------------------------------------ */
/*  The stages                                                         */
/* ------------------------------------------------------------------ */

function ScatteredStage({ on }: { on: boolean }) {
  return (
    <>
      <ScatteredDots opacity={on ? 1 : 0} />
      {SCATTERED.map((chip, index) => (
        <Chip key={chip.label} {...chip} shown={on} delay={index * 60} />
      ))}
    </>
  );
}

function FollowedStage({ on }: { on: boolean }) {
  return (
    <>
      <ScatteredDots opacity={on ? 0.28 : 0} />
      <svg viewBox={`0 0 ${WIDE} ${TALL}`} className="absolute inset-0 h-full w-full">
        {on
          ? FOLLOWED.map(({ label, at }, index) => {
              const x = (at[0] / 100) * WIDE;
              const y = (at[1] / 100) * TALL;
              return (
                <path
                  key={label}
                  d={`M${WIDE / 2} ${TALL / 2} Q${WIDE / 2} ${y} ${x} ${y}`}
                  pathLength={1}
                  className={ui.draw}
                  fill="none"
                  stroke="var(--ui-primary)"
                  strokeOpacity={0.55}
                  strokeWidth={1.5}
                  style={{ "--delay": `${150 + index * 110}ms` } as CSSProperties}
                />
              );
            })
          : null}
      </svg>
      {FOLLOWED.map((chip, index) => (
        <Chip key={chip.label} {...chip} shown={on} delay={350 + index * 110} />
      ))}
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <span {...FRAME_CORNERS} className={`${ui.panel} ${ui.lifted} flex flex-col items-center px-5 py-3`} style={enter(on, { y: 0, blur: 8, duration: 500 })}>
          <TwinMark className="size-7" />
          <strong style={DISPLAY} className="mt-1.5 text-[14px] leading-tight font-semibold">
            {NORTHSTAR_ANSWER.entity}
          </strong>
        </span>
      </span>
    </>
  );
}

/* The Twin gathering: the hero's sequence joined where the particles swarm
 * loose in the glass, a little before they sort into their rings, and played
 * to the settled rings. */
const GATHER_FROM_SECONDS = 14.6;
const GATHER_RATE = 2.6;
/** The hero draws its particles at 2.25 in a 332px glass; the same grain at this stage's size. */
const GATHER_PARTICLE_SCALE = 2.85;

/**
 * The shared understanding is the Twin itself: the hero's own particles
 * (LiveInstrument, without its dial), so the rings here are the hero's
 * rings. They gather once, when the step comes into view and the twin can
 * draw; with reduced motion the renderer shows them settled.
 */
function UnderstandingStage({ on }: { on: boolean }) {
  const [ready, setReady] = useState(false);
  const [startMs, setStartMs] = useState<number | null>(null);
  useEffect(() => {
    if (on && ready) setStartMs((current) => current ?? performance.now());
  }, [on, ready]);
  const clock = useMemo(() => runOnceTwinClock(GATHER_RATE, startMs, GATHER_FROM_SECONDS), [startMs]);
  const markReady = useCallback(() => setReady(true), []);
  return (
    <TwinClockProvider value={clock}>
      <div className="absolute inset-0" style={{ opacity: on ? 1 : 0, transition: "opacity 600ms ease-out" }}>
        <LiveInstrument bare sizeClass="w-full" scale={GATHER_PARTICLE_SCALE} onReady={markReady} />
      </div>
    </TwinClockProvider>
  );
}

/** A drawn stage: the wide one the sources lie on, or the square one the Twin gathers in. */
function Stage({ square = false, children }: { square?: boolean; children: ReactNode }) {
  return (
    <div aria-hidden="true" className={`relative mx-auto w-full self-center ${square ? "aspect-square max-w-[26.25rem]" : "aspect-[52/33] max-w-[32.5rem]"}`}>
      {children}
    </div>
  );
}

/** The scroll story's stack of tool cards: the same answer in Twin chat, ChatGPT, Claude, Gemini, Copilot and the terminal. */
function AnswerStage({ on }: { on: boolean }) {
  return (
    // The cards behind the front one sit up to 40px above the stack's box.
    <div className={`${styles.answers} mx-auto w-full max-w-[28rem] self-center pt-10`} style={enter(on, { y: 12, duration: 650 })}>
      <ConnectedAnswer active={on} dense />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  The steps                                                          */
/* ------------------------------------------------------------------ */

/** A chapter of the scroll story, by its name there. */
function chapter(name: string) {
  const found = CHAPTERS.find((candidate) => candidate.name === name);
  if (!found) throw new Error(`The story has no chapter "${name}".`);
  return found;
}

const OPENING = chapter("Scattered sources");
/** The opening chapter's two sentences: the first is the first step's, the second the section's. */
const [OPENING_PAIN, OPENING_PROMISE] = OPENING.body.split(/(?<=\.)\s+/) as [string, string];

/** The section's head and lede, for the band that stages the steps. */
export const STORY_STEPS_HEAD = OPENING.title.replace("\n", " ");
export const STORY_STEPS_LEDE = OPENING_PROMISE;

const STEPS: readonly { name: string; body: string; stage: (on: boolean) => ReactNode }[] = [
  {
    name: OPENING.name,
    body: OPENING_PAIN,
    stage: (on) => (
      <Stage>
        <ScatteredStage on={on} />
      </Stage>
    ),
  },
  {
    name: "Follow Northstar",
    body: chapter("Follow Northstar").body,
    stage: (on) => (
      <Stage>
        <FollowedStage on={on} />
      </Stage>
    ),
  },
  {
    name: "A shared understanding",
    body: chapter("A shared understanding").body,
    stage: (on) => (
      <Stage square>
        <UnderstandingStage on={on} />
      </Stage>
    ),
  },
  { name: "A useful answer", body: chapter("A useful answer").body, stage: (on) => <AnswerStage on={on} /> },
];

/* A step builds once it has come into view: the left of a row at once, the
 * right a beat after, so a row reads left to right. Two steps each: waiting,
 * then built. */
const BUILD_FIRST = [150, 0] as const;
const BUILD_SECOND = [950, 0] as const;

/** Whether the element has come into view; once it has, it stays so. */
function useSeen<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element || seen) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setSeen(true);
      },
      { threshold: 0.3 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [seen]);
  return [ref, seen];
}

function Step({ index }: { index: number }) {
  const step = STEPS[index]!;
  const [item, seen] = useSeen<HTMLLIElement>();
  const second = index % 2 === 1;
  const built = useTimeline(second ? BUILD_SECOND : BUILD_FIRST, { loop: false, run: seen }) > 0;
  return (
    <li
      ref={item}
      className={`grid min-w-0 border-dotted border-[var(--rule)] lg:row-span-3 lg:grid-rows-subgrid ${second ? "lg:pl-12" : "lg:border-r lg:pr-12"} ${
        index < 2 ? "lg:pb-12" : "lg:border-t lg:pt-12"
      }`}
    >
      {step.stage(built)}
      <h3 style={DISPLAY} className={`mt-7 flex items-baseline gap-2.5 text-[1.15rem] leading-[1.3] font-semibold ${INK_88}`}>
        <span className={`${DATA_LABEL_CLS} text-[var(--theme-accent-text)]`}>{String(index + 1).padStart(2, "0")}</span>
        {step.name}
      </h3>
      <p className={`mt-2 max-w-[52ch] text-[0.95rem] leading-[1.65] ${INK_72}`}>{step.body}</p>
    </li>
  );
}

export function StorySteps() {
  return (
    <UiRoot>
      {/* From 1024px: two rows of two on one grid. Each step's three parts
          (stage, name, words) sit on rows its neighbour shares, so the names
          sit level under stages of different heights. A dotted rule parts
          the columns and another the rows. */}
      <ol className="m-0 grid list-none gap-y-14 p-0 lg:grid-cols-2 lg:gap-y-0">
        {STEPS.map((step, index) => (
          <Step key={step.name} index={index} />
        ))}
      </ol>
    </UiRoot>
  );
}
